import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Container, Paper, useTheme, Tooltip, Button, alpha } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Grid from "@mui/material/Grid";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

import { useReportFilters } from "./hooks/useReportFilters";
import { useReportsData } from "./hooks/useReportsData";
import { usePageHeader } from "../../context/LayoutContext";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { ReportFilters } from "./components/ReportFilters";
import { SortControls } from "./components/SortControls";
import { EmptyReportsState } from "./components/EmptyReportsState";
import { ReportCard } from "./components/ReportCard";
import { ReportCardSkeleton } from "./components/ReportCardSkeleton";

import { useQuery } from "@tanstack/react-query";
import { stationDirectoryQuery } from "../../features/users/api/usersQueries";
import { useOrgId } from "../../shared/auth/orgContext";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { canReadUsers } from "../../shared/auth/authorization";
import InactivityRedirectModal from "@/shared/ui/modals/InactivityRedirectModal";
import { ConfirmDialog, ErrorState } from "../../shared/ui";
import PaginationControls from "../../components/ui/PaginationControls";
import { useToast } from "../../shared/toast";
import { JobRowSkeleton } from "../../components/skeletons/JobRowSkeleton";
import { useJobStore } from "../../store/jobStore";
import { ReportJobStatus } from "../../shared/interfaces/JobStatus";
import { ReportStatus } from "../../shared/interfaces/ReportStatus";

// Enable dayjs plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const HomePage: React.FC = () => {
  const theme = useTheme();
  const vars = theme;
  const navigate = useNavigate();
  const location = useLocation();
  // Station directory for the reporter/modifier filter pickers. Shares the
  // cache entry the synchronous getUserName reads — one fetch serves both.
  // Gated exactly like the old provider fetch (org present + read-users
  // permission); fetches on first Home mount instead of shell mount.
  const organizationId = useOrgId();
  const permissions = usePermissions();
  const { data: stationDirectory } = useQuery({
    ...stationDirectoryQuery(organizationId ?? ""),
    enabled: !!organizationId && canReadUsers(permissions),
  });
  const stationOfficers = stationDirectory?.users;

  // Hooks
  const toast = useToast();

  const [page, setPage] = useState(0);
  const [fetchError, setFetchError] = useState<string>("");
  const filters = useReportFilters(setPage);

  const showFetchError = useCallback(
    (message: string) => {
      setFetchError(message);
    },
    [],
  );

  const {
    reports,
    totalReports,
    isLoading,
    isInitialLoad,
    rowsPerPage,
    setRowsPerPage,
    sortConfig,
    requestSort,
    openDeleteModal,
    reportToDelete,
    handleReportDelete,
    handleModalClose,
    handleOpenDeleteModal,
    fetchReports,
  } = useReportsData(filters, page, showFetchError, toast.success);

  // Inactivity Modal State
  const [inactivityModalOpen, setInactivityModalOpen] = useState(false);
  const [inactivityMessage, setInactivityMessage] = useState<string>("");

  const jobs = useJobStore((state) => state.jobs);
  const pendingJobs = jobs.filter(
    (job) => job.status === ReportJobStatus.PENDING,
  );

  // Computed data for filters — one list serves both the reporter and
  // modifier filters. Invitation-only directory rows (id null) can't have
  // authored anything.
  const availableOfficers = useMemo(() => {
    if (!stationOfficers) return [];
    return stationOfficers
      .flatMap((officer) =>
        officer.id
          ? [{ name: `${officer.firstName} ${officer.lastName}`, id: officer.id }]
          : [],
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [stationOfficers]);

  const allReportStatuses = useMemo(() => {
    // Offer every status in the filter picker regardless of the flag. A report's
    // stored status is canonical and never reclassified by a flag toggle, so a
    // flag-off station can still hold (and legitimately filter by) CO-track
    // reports created while the flag was on. The API honors these filters.
    // GENERATING is omitted: those reports are surfaced by the app-level
    // Report Tracker, not the /reports list, so filtering by it never returns anything.
    return (Object.values(ReportStatus) as ReportStatus[])
      .filter((v) => typeof v === "string")
      .filter((v) => v !== ReportStatus.GENERATING)
      .sort((a, b) => (a || "").toString().localeCompare((b || "").toString()));
  }, []);

  // Effects
  useEffect(() => {
    const state =
      (location.state as { message?: string } | undefined) || undefined;
    if (state?.message) {
      setInactivityMessage(state.message);
      setInactivityModalOpen(true);
      navigate(location.pathname, { replace: true, state: undefined });
    }
  }, [location.state, location.pathname, navigate]);

  // Reset page when filters change
  useEffect(() => {
    if (setPage) {
      // Guard in case useReportsData hasn't been updated yet
      setPage(0);
    }
  }, [
    filters.dateFrom,
    filters.dateTo,
    filters.reportedDateFrom,
    filters.reportedDateTo,
    filters.debouncedDetailsFilter,
    filters.reportedByFilter,
    filters.modifiedByFilter,
    filters.selectedStatuses,
    setPage,
  ]);

  const hasActiveFilters =
    filters.dateFrom !== null ||
    filters.dateTo !== null ||
    filters.reportedDateFrom !== null ||
    filters.reportedDateTo !== null ||
    filters.debouncedDetailsFilter.trim() !== "" ||
    filters.reportedByFilter.length > 0 ||
    filters.modifiedByFilter.length > 0 ||
    filters.selectedStatuses.length > 0;

  // Set page header in app bar
  usePageHeader({
    title: "My Reports",
    subtitle: "View, filter, and manage your incident reports",
    icon: <AssessmentIcon />,
    // Until the first load settles, show a wave indicator instead of the
    // (still-zero) count; afterwards refetches keep the last count.
    chip: {
      label: isInitialLoad ? "Reports" : `${totalReports} Reports`,
      loading: isInitialLoad,
    },
  });

  return (
    <Container sx={{ overflow: "auto" }}>

      <ConfirmDialog
        open={openDeleteModal}
        tone="error"
        title={`Are You Sure You Want to Delete ${reportToDelete?.reportName}?`}
        description="This action cannot be undone."
        onClose={handleModalClose}
        onConfirm={handleReportDelete}
      />

      <InactivityRedirectModal
        open={inactivityModalOpen}
        onAcknowledge={() => setInactivityModalOpen(false)}
        message={inactivityMessage}
      />

      <Grid
        container
        spacing={3}
        direction="column"
        sx={{ display: "flex", alignItems: "stretch", py: 2 }}
      >
        <Grid size={{ xs: 12 }}>
          <ReportFilters
            filters={filters}
            availableReporters={availableOfficers}
            availableModifiers={availableOfficers}
            allReportStatuses={allReportStatuses}
          />
        </Grid>

        <Grid
          size={{ xs: 12 }}
          container
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Grid size={{ xs: 12, md: "auto" }} sx={{ overflow: "hidden" }}>
            <SortControls sortConfig={sortConfig} onSortChange={requestSort} />
          </Grid>
          <Grid size={{ xs: 12, md: "auto" }}>
            <Tooltip title="Create a new incident report" arrow>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/video-selection")}
                sx={{
                  height: 48,
                  px: 4,
                  textTransform: "none",
                  borderRadius: 2,
                  fontSize: "1rem",
                  fontWeight: 600,
                  width: { xs: "100%", md: "auto" },
                  color: "#fff",
                  background: `linear-gradient(135deg, ${vars.palette.primary.main} 0%, ${vars.palette.primary.dark} 100%)`,
                  boxShadow: `0 4px 14px 0 ${alpha(
                    vars.palette.primary.main,
                    0.4,
                  )}`,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 6px 20px 0 ${alpha(
                      vars.palette.primary.main,
                      0.5,
                    )}`,
                    background: `linear-gradient(135deg, ${vars.palette.primary.light} 0%, ${vars.palette.primary.main} 100%)`,
                  },
                  "&:active": {
                    transform: "translateY(0)",
                    boxShadow: `0 2px 8px 0 ${alpha(
                      vars.palette.primary.main,
                      0.4,
                    )}`,
                  },
                  "&:focus-visible": {
                    outline: `3px solid ${alpha(
                      vars.palette.primary.main,
                      0.5,
                    )}`,
                    outlineOffset: 2,
                  },
                }}
              >
                New Report
              </Button>
            </Tooltip>
          </Grid>
        </Grid>

        <Grid size={{ xs: 12 }}>
          {fetchError && (
            <ErrorState
              variant="inline"
              retryLabel="Refresh"
              message={fetchError}
              onRetry={() => {
                setFetchError("");
                fetchReports();
              }}
            />
          )}

          <Grid container spacing={2}>
            {isLoading ? (
              Array.from(new Array(rowsPerPage)).map((_, index) => (
                <Grid size={{ xs: 12 }} key={index}>
                  <ReportCardSkeleton />
                </Grid>
              ))
            ) : (
              <>
                {pendingJobs.map((job) => (
                  <Grid size={{ xs: 12 }} key={`job-${job.id}`}>
                    <JobRowSkeleton
                      reportName={
                        job.reportName ||
                        `Report from ${job.evidenceIDs.length} video(s)`
                      }
                      onClick={() => navigate(`/generating/${job.id}`)}
                    />
                  </Grid>
                ))}

                {reports.length > 0 ? (
                  reports.map((report) => (
                    <Grid size={{ xs: 12 }} key={report.id}>
                      <ReportCard
                        report={report}
                        onDelete={handleOpenDeleteModal}
                      />
                    </Grid>
                  ))
                ) : pendingJobs.length === 0 ? (
                  <Grid size={{ xs: 12 }}>
                    <EmptyReportsState
                      hasActiveFilters={hasActiveFilters}
                      onResetFilters={filters.handleResetFilters}
                    />
                  </Grid>
                ) : null}
              </>
            )}
          </Grid>

          {totalReports > 0 && (
            <Paper
              elevation={0}
              sx={{
                mt: 3,
                mb: 2,
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "grey.400",
                bgcolor: "background.paper",
              }}
            >
              <PaginationControls
                currentPage={page}
                totalItems={totalReports}
                rowsPerPage={rowsPerPage}
                itemLabel="reports"
                onPageChange={setPage}
                onRowsPerPageChange={(n) => {
                  setRowsPerPage(n);
                  setPage(0);
                }}
              />
            </Paper>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default HomePage;
